import { fetchHighIntentOffers, fetchPersonalisedAlternativeOffers } from 'actions/RecommendationActions'
import { getListName } from 'analytics/enhanced-ecommerce'
import { homePageOfferClickEvent, hotelsPageOfferClickEvent } from 'analytics/optimizely/events'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import Pane from 'components/Common/Pane/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel/TrackedCardCarousel'
import TopPicksForMeCarouselOffer from 'components/Recommendations/TopPicksForMe/TopPicksForMeCarouselOffer'
import TopPicksForMeOfferSkeleton from 'components/Recommendations/TopPicksForMe/TopPicksForMeOfferSkeleton'
import { TrackingProps } from 'contexts/trackingContext'
import OfferCarouselHeader from 'home/components/OfferCarousels/common/OfferCarouselHeader'
import { useAppDispatch } from 'hooks/reduxHooks'
import { take, without } from 'lib/arrayUtils'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'
import uuidV4 from 'lib/uuidV4'
import { rem } from 'polished'
import React, { useEffect, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import { getCurrentUserId } from 'selectors/accountSelectors'
import styled from 'styled-components'
import * as Analytics from 'analytics/analytics'
import { useIsHotelsPage } from 'hooks/useIsHotelsPage'
import { useIsOldHomePage } from 'home/hooks/isHomepage'

interface Props {
  alternatives: App.RecommendationState['personalisedAlternatives'];
  highIntent: App.RecommendationState['highIntent'];
  userId?: string;
  userGivenName?: string;
  trackingPageSource: string;
  abTestKey: OptimizelyServerSideExperiments;
}

const Section = styled(Pane)`
  padding-top: ${rem(24)};
  padding-bottom: ${rem(24)};
`

const TRACKING_UI_TOUCH_POINT = 'Recommended for You Carousel V1.0.0'

function TopPicksForMeCarousel({
  alternatives,
  highIntent,
  userId,
  userGivenName,
  trackingPageSource,
  abTestKey,
}: Props) {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchPersonalisedAlternativeOffers())
    dispatch(fetchHighIntentOffers())
  }, [dispatch])

  const offerIds = useMemo(() => {
    return alternatives.offers.map(offer => offer.offerId)
  }, [alternatives.offers])

  const highIntentOfferIds = useMemo(() => {
    return highIntent.offers.map(offer => offer.offerId)
  }, [highIntent.offers])

  const tracking = useMemo<TrackingProps>(() => {
    const lere = alternatives.lereVersion ? { version: alternatives.lereVersion } : undefined
    return {
      category: 'LERE',
      label: TRACKING_UI_TOUCH_POINT,
      listId: uuidV4(),
      listName: getListName('LERE', trackingPageSource, TRACKING_UI_TOUCH_POINT),
      lere,
    }
  }, [alternatives.lereVersion, trackingPageSource])

  const isHotelsPage = useIsHotelsPage()
  const isOldHomePage = useIsOldHomePage()

  const onClick = useCallback(() => {
    if (isHotelsPage) {
      Analytics.track('click-offer-on-hotels-page', hotelsPageOfferClickEvent('TPFM carousel'))
    } else if (isOldHomePage) {
      Analytics.track('click-old-homepage-tile-including-recommendation', homePageOfferClickEvent('TPFM carousel'))
    }
  }, [isHotelsPage, isOldHomePage])

  const finalOfferIds = useMemo(() => without(offerIds, ...highIntentOfferIds),
    [highIntentOfferIds, offerIds])

  const isCarouselEnabledByAbTest = !!useOptimizelyExperiment(abTestKey, finalOfferIds.length > 0)

  const isLoading = alternatives.state === 'loading' || highIntent.state === 'loading'

  if (!userId || !finalOfferIds.length || !isCarouselEnabledByAbTest) {
    return null
  }

  return (
    <Section>
      <LayoutContainer>
        <VerticalSpacer gap={20}>
          <OfferCarouselHeader allPath="/top-picks-for-me" variant="heading3">
            Top picks for <i>{userGivenName ?? 'you'}</i>
          </OfferCarouselHeader>
          <TrackedCardCarousel width="90%" pageSize={3}>
            {isLoading && <>
              <TopPicksForMeOfferSkeleton />
              <TopPicksForMeOfferSkeleton />
              <TopPicksForMeOfferSkeleton />
            </>}
            {!isLoading && take(finalOfferIds, 25).map((id, idx) => {
              const position = idx + 1

              return <TopPicksForMeCarouselOffer
                key={id}
                offerId={id}
                position={position}
                trackingProps={tracking}
                preload={position <= 6}
                onClick={onClick}
              />
            })}
          </TrackedCardCarousel>
        </VerticalSpacer>
      </LayoutContainer>
    </Section>
  )
}

function mapState(state: App.State) {
  return {
    alternatives: state.recommendations.personalisedAlternatives,
    highIntent: state.recommendations.highIntent,
    userId: getCurrentUserId(state),
    userGivenName: state.auth.account.givenName,
  }
}

export default connect(mapState)(TopPicksForMeCarousel)
